import React, { useEffect, useState, useCallback } from "react";
import { Spin, Button, Input, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { postApi } from "../../redux/rest/restSlice";
import {
  LIST_ADVERTISEMENT,
  CREATE_ADVERTISEMENT,
} from "../../constants/actionTypes";
import { unwrapResult } from "@reduxjs/toolkit";
import get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";
import {
  advertisementTables,
  AdvertisementForm,
} from "../../constants/advertisementTables";
import Can from "../Can";
import DataTable from "../DataTable";
import { PERMISSIONS } from "../../constants/permissions";
import { hasAllPermissions } from "../../redux/selectors/authSelector";
import { TABLE_PAGINATION } from "../../utilities/helper";

const AdvertisementOverviewTable = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const dispatch = useDispatch();
  const canUpdateAdvertisement = useSelector(
    hasAllPermissions([PERMISSIONS.UPDATE_ADVERTISEMENTS])
  );

  // Refreshes keep the current search (also used by update/delete in the table)
  const fetchAdvertisementList = useCallback(() => {
    setLoading(true);
    dispatch(
      postApi({
        endpoint: LIST_ADVERTISEMENT,
        // Same request as before (no body) unless searching
        ...(appliedSearch
          ? { payload: { filters: { advertisement_name: appliedSearch } } }
          : {}),
      })
    )
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        setData(get(result, "data"));
      })
      .catch(() => {
        setLoading(false);
      });
  }, [dispatch, appliedSearch]);

  const onSearch = (value) => {
    const term = (value || "").trim();

    if (term === appliedSearch) {
      fetchAdvertisementList();
    } else {
      // Refetches through the effect below
      setAppliedSearch(term);
    }
  };

  const clearSearch = () => {
    setSearchValue("");
    onSearch("");
  };

  const onFinish = (values) => {
    setLoading(true);
    const payload = {
      advertisement_name: values.advertisement_name,
      advertisement_url: values.advertisement_url,
    };

    dispatch(
      postApi({
        endpoint: CREATE_ADVERTISEMENT,
        payload,
      })
    )
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        if (get(result, "success")) {
          message.success(get(result, "message"));
          setModalVisible(false);
          fetchAdvertisementList(); // Refresh the table
        }
      })
      .catch(() => {
        setLoading(false);
        message.error("Failed to create advertisement.");
      });
  };

  useEffect(() => {
    fetchAdvertisementList();
  }, [fetchAdvertisementList]);

  const rows = get(data, "data");
  const firstLoad = loading && !(rows && rows.length);

  return (
    <>
      <div className="list-toolbar">
        <div className="list-toolbar__main">
          <Input.Search
            className="list-toolbar__search"
            placeholder="Search by Advertisement Name"
            allowClear
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={onSearch}
          />
        </div>

        <Can permissions={[PERMISSIONS.CREATE_ADVERTISEMENTS]}>
          <div className="list-toolbar__actions">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
            >
              Create Advertisement
            </Button>
          </div>
        </Can>
      </div>
      <AdvertisementForm
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={onFinish}
      />
      <Spin spinning={loading && !firstLoad}>
        <DataTable
          skeleton={firstLoad}
          scrollX={600}
          rowKey="id"
          className="mt-20"
          dataSource={rows}
          columns={advertisementTables(
            fetchAdvertisementList,
            canUpdateAdvertisement
          )}
          pagination={TABLE_PAGINATION}
          emptyProps={{
            noun: "advertisements",
            search: appliedSearch,
            onClearSearch: clearSearch,
          }}
        />
      </Spin>
    </>
  );
};

export default AdvertisementOverviewTable;
